import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../utils/authToken';
import { searchPurchaseFormUsers } from '../utils/searchUsers';
import type { PurchaseSubtaskTargetType, User } from '../types';

const USER_SEARCH_DEBOUNCE_MS = 350;

export interface PurchaseSubtaskSubmitPayload {
  title: string;
  description: string;
  priority: string;
  subtaskTypeId?: number;
  targetType: PurchaseSubtaskTargetType;
  assignedTo?: number;
}

interface SubtaskType {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
  default_priority?: string;
  workflow_id_for_subtask?: number;
}

interface PriorityOption {
  value: string;
  label: string;
}

interface PurchaseSubtaskModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (payload: PurchaseSubtaskSubmitPayload) => void;
  workflowId?: number;
  isLoading?: boolean;
  canSetPriority?: boolean;
  priorityOptions: PriorityOption[];
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  analista: 'Analista',
  gerente: 'Gerente',
  usuario: 'Usuário',
};

const PurchaseSubtaskModal: React.FC<PurchaseSubtaskModalProps> = ({
  show,
  onHide,
  onSubmit,
  workflowId,
  isLoading = false,
  canSetPriority = false,
  priorityOptions,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('p3');
  const [subtaskTypeId, setSubtaskTypeId] = useState<string>('');
  const [subtaskTypes, setSubtaskTypes] = useState<SubtaskType[]>([]);

  const [targetType, setTargetType] = useState<PurchaseSubtaskTargetType>('group');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [debouncedUserQuery, setDebouncedUserQuery] = useState('');
  const [userResults, setUserResults] = useState<User[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setPriority('p3');
    setSubtaskTypeId('');
    setTargetType('group');
    setUserSearchTerm('');
    setDebouncedUserQuery('');
    setUserResults([]);
    setSelectedUser(null);
  }, []);

  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [show, resetForm]);

  useEffect(() => {
    let cancelled = false;
    const loadSubtaskTypes = async () => {
      if (!show || !workflowId) {
        setSubtaskTypes([]);
        return;
      }
      try {
        const response = await axios.get<SubtaskType[]>(
          `${API_URL}/api/workflows/${workflowId}/subtask-types`,
        );
        if (!cancelled) {
          setSubtaskTypes((response.data || []).filter((type) => type.is_active));
        }
      } catch {
        if (!cancelled) {
          setSubtaskTypes([]);
        }
      }
    };

    loadSubtaskTypes();
    return () => {
      cancelled = true;
    };
  }, [show, workflowId]);

  useEffect(() => {
    if (!subtaskTypeId) return;
    const selectedType = subtaskTypes.find((type) => type.id === parseInt(subtaskTypeId, 10));
    if (selectedType?.default_priority) {
      setPriority(selectedType.default_priority);
    }
  }, [subtaskTypeId, subtaskTypes]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedUserQuery(userSearchTerm), USER_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [userSearchTerm]);

  useEffect(() => {
    if (targetType !== 'user') {
      setUserResults([]);
      setUserSearchLoading(false);
      return;
    }
    const query = debouncedUserQuery.trim();
    if (query.length < 1) {
      setUserResults([]);
      setUserSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    setUserSearchLoading(true);
    (async () => {
      try {
        const items = await searchPurchaseFormUsers(query, getAuthToken(), {
          limit: 40,
          signal: controller.signal,
        });
        setUserResults(items.filter((user) => user.is_active));
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') return;
        setUserResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    })();

    return () => controller.abort();
  }, [debouncedUserQuery, targetType]);

  const handleTargetTypeChange = (nextTargetType: PurchaseSubtaskTargetType) => {
    setTargetType(nextTargetType);
    if (nextTargetType === 'group') {
      setSelectedUser(null);
      setUserSearchTerm('');
      setUserResults([]);
    }
  };

  const isTargetValid = targetType === 'group' || !!selectedUser;
  const canSubmit = useMemo(
    () => !!title.trim() && !!description.trim() && isTargetValid && !isLoading,
    [title, description, isTargetValid, isLoading],
  );

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      title,
      description,
      priority,
      subtaskTypeId: subtaskTypeId ? parseInt(subtaskTypeId, 10) : undefined,
      targetType,
      assignedTo: targetType === 'user' ? selectedUser?.id : undefined,
    });
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Nova Subtarefa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {subtaskTypes.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Tipo de Subtarefa</Form.Label>
            <Form.Select value={subtaskTypeId} onChange={(e) => setSubtaskTypeId(e.target.value)}>
              <option value="">Selecione um tipo (opcional)</option>
              {subtaskTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} {type.description ? `- ${type.description}` : ''}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Selecione o tipo de subtarefa. Se não selecionar, a subtarefa usará o fluxo da requisição pai.
            </Form.Text>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Título <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Descrição <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Direcionar para <span className="text-danger">*</span></Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              id="subtask-target-group"
              name="subtask-target"
              label="Grupo do fluxo"
              checked={targetType === 'group'}
              onChange={() => handleTargetTypeChange('group')}
            />
            <Form.Check
              inline
              type="radio"
              id="subtask-target-user"
              name="subtask-target"
              label="Usuário específico"
              checked={targetType === 'user'}
              onChange={() => handleTargetTypeChange('user')}
            />
          </div>
          {targetType === 'group' ? (
            <Form.Text className="text-muted">
              A subtarefa entra na fila do grupo definido pelo fluxo e será distribuída a um analista.
            </Form.Text>
          ) : (
            <Form.Text className="text-muted">
              A subtarefa vai direto para a pessoa escolhida, sem passar por fila de grupo.
            </Form.Text>
          )}
        </Form.Group>

        {targetType === 'user' && (
          <Form.Group className="mb-3">
            <Form.Label>Usuário responsável <span className="text-danger">*</span></Form.Label>
            {selectedUser ? (
              <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2">
                <div>
                  <div className="fw-semibold">{selectedUser.name}</div>
                  <div className="text-muted small">{selectedUser.email}</div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="secondary">{ROLE_LABELS[selectedUser.role] || selectedUser.role}</Badge>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => {
                      setSelectedUser(null);
                      setUserSearchTerm('');
                    }}
                  >
                    Trocar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Form.Control
                  type="text"
                  placeholder="Busque por nome ou e-mail"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  autoComplete="off"
                />
                {userSearchLoading && (
                  <div className="text-muted small mt-2 d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" /> Buscando usuários...
                  </div>
                )}
                {!userSearchLoading && userResults.length > 0 && (
                  <ListGroup className="mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {userResults.map((user) => (
                      <ListGroup.Item
                        key={user.id}
                        action
                        onClick={() => setSelectedUser(user)}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <div>{user.name}</div>
                          <div className="text-muted small">{user.email}</div>
                        </div>
                        <Badge bg="secondary">{ROLE_LABELS[user.role] || user.role}</Badge>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
                {!userSearchLoading && debouncedUserQuery.trim().length > 0 && userResults.length === 0 && (
                  <div className="text-muted small mt-2">Nenhum usuário ativo encontrado.</div>
                )}
              </>
            )}
          </Form.Group>
        )}

        {canSetPriority && (
          <Form.Group className="mb-3">
            <Form.Label>Prioridade</Form.Label>
            <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
            {subtaskTypeId && (
              <Form.Text className="text-muted">
                Prioridade padrão do tipo de subtarefa selecionado aplicada automaticamente.
              </Form.Text>
            )}
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={!canSubmit}>
          {isLoading ? 'Criando...' : 'Criar Subtarefa'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PurchaseSubtaskModal;
